import {forwardRef, InputHTMLAttributes} from 'react'

import cn from 'classnames/bind'

import s from './Select.module.scss'
import ErrorMessage from '../ErrorMessage'
import FieldLabel from '../FieldLabel'
import {IFieldBase} from '../Input/Input'

const cx = cn.bind(s)

type TInputHTMLAttributes = Omit<InputHTMLAttributes<HTMLSelectElement>, 'size'>

export interface ISelectProps extends TInputHTMLAttributes, IFieldBase {
	variant?: 'default' | 'editable' | 'new'
	size?: 'medium' | 'large'
	wrapperClassName?: string
	options: readonly {value: string | number; name?: string}[]
}

const Select = forwardRef<HTMLSelectElement, ISelectProps>(
	(
		{
			className,
			variant = 'default',
			size = 'medium',
			wrapperClassName,
			label,
			options,
			isError,
			error,
			value,
			...props
		},
		ref,
	) => {
		const {placeholder, ...otherProps} = props
		const content = (
			<select
				className={cx('select', variant, size, className)}
				required
				value={value || ''}
				ref={ref}
				{...otherProps}>
				{Array.isArray(options) && (
					<>
						<option
							label={placeholder || 'Select option'}
							className={cx('option')}
							hidden
							disabled
							value="">
							{placeholder}
						</option>
						{options.map(({value: optionValue, name: optionName}) => (
							<option className={cx('option')} key={optionValue} value={optionValue}>
								{optionName || optionValue}
							</option>
						))}
					</>
				)}
			</select>
		)

		return (
			<div className={cx('wrapper', wrapperClassName, variant, {isError: !!error || isError})}>
				<FieldLabel label={label} variant={variant} required={props.required}>
					{content}
				</FieldLabel>
				<ErrorMessage error={error} />
			</div>
		)
	},
)

Select.displayName = 'Select'

export default Select
